import { Box } from "@mui/material";
import { type FC, useEffect, useMemo } from "react";
import { GoalDetailOwnFunds } from "web_myplann/Goals/Components/GoalDetailOwnFunds";
import { GoalDetailSolution } from "web_myplann/Goals/Components/GoalDetailSolution";
import { NewContractFlow } from "web_myplann/Goals/Components/PinContract/NewContractFlow";
import { PinContractChoiceDrawer } from "web_myplann/Goals/Components/PinContract/PinContractChoiceDrawer";
import { PinExistingContractDrawer } from "web_myplann/Goals/Components/PinContract/PinExistingContractDrawer";
import { getGoalPortfoliosByType } from "web_myplann/Goals/Helpers/getGoalPortfoliosByType";
import { useGoalPinStep } from "web_myplann/Goals/Hooks/useGoalPinStep";
import { useGoalPortfolioActions } from "web_myplann/Goals/Hooks/useGoalPortfolioActions";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import type { PinStep } from "web_myplann/Goals/Types/PinStep";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";

export type WelcomePinTrigger = { hasOwnFunds: boolean };

type Props = {
	goal: GoalDetail;
	goalDto: GoalDetailResponseDTO | undefined;
	welcomePinTrigger: WelcomePinTrigger | null;
	onWelcomePinTriggerHandled: () => void;
	onLockedAction: (() => void) | null;
};

export const GoalPinningSection: FC<Props> = ({
	goal,
	goalDto,
	welcomePinTrigger,
	onWelcomePinTriggerHandled,
	onLockedAction,
}) => {
	const {
		pinStep,
		pinPortfolioType,
		closePin,
		goToStep,
		openPin,
		openPinStep,
		openOwnFundsPin,
	} = useGoalPinStep(onLockedAction);

	const { pin, unpin, editAllocation, pinningId, unpinningId } =
		useGoalPortfolioActions({
			goalId: Number(goal.id),
			goalDto,
			onPinSettled: closePin,
		});

	useEffect(() => {
		if (!welcomePinTrigger) return;
		if (!onLockedAction) {
			const { hasOwnFunds } = welcomePinTrigger;
			openPin(
				hasOwnFunds
					? GoalPortfolioTypeEnum.SetAside
					: GoalPortfolioTypeEnum.Allocated,
				hasOwnFunds ? "choose" : "existing",
			);
		}
		onWelcomePinTriggerHandled();
	}, [welcomePinTrigger, onWelcomePinTriggerHandled, onLockedAction, openPin]);

	const handleSolutionPinStep = (next: PinStep) => {
		if (next === "closed") {
			closePin();
			return;
		}
		openPinStep(next);
	};

	const handlePinSelect = (picked: GoalPortfolioListItemResponseDTO) => {
		pin(picked, pinPortfolioType);
	};

	const handlePinCreated = (portfolioId: number, onSettled: () => void) => {
		pin({ id: portfolioId, usagePercent: 0 }, GoalPortfolioTypeEnum.SetAside, {
			onSettled,
		});
	};

	const isSetAside = pinPortfolioType === GoalPortfolioTypeEnum.SetAside;

	const { allocated, setAside } = useMemo(
		() => getGoalPortfoliosByType(goal.portfolios),
		[goal.portfolios],
	);

	const hasOwnFunds = goal.ownFunds > 0;
	const readOnly = goal.completed;

	return (
		<>
			{hasOwnFunds && (
				<Box mt={1}>
					<GoalDetailOwnFunds
						goal={goal}
						portfolios={setAside}
						readOnly={readOnly}
						onAddPin={openOwnFundsPin}
						onUnpin={unpin}
						unpinningId={unpinningId}
						onLockedAction={onLockedAction}
					/>
				</Box>
			)}
			<Box mt={1}>
				<GoalDetailSolution
					goal={goal}
					allocatedPortfolios={allocated}
					pinStep={pinStep}
					readOnly={readOnly}
					onPinStepChange={handleSolutionPinStep}
					onUnpin={unpin}
					onEditAllocation={editAllocation}
					unpinningId={unpinningId}
					onLockedAction={onLockedAction}
				/>
			</Box>
			<PinContractChoiceDrawer
				open={!readOnly && pinStep === "choose"}
				portfolioType={pinPortfolioType}
				onClose={closePin}
				onPickExisting={() => goToStep("existing")}
				onPickNew={() => goToStep("new")}
				onPickFuture={() => goToStep("future")}
			/>
			<PinExistingContractDrawer
				open={!readOnly && pinStep === "existing"}
				onClose={closePin}
				onSelect={handlePinSelect}
				onAddNew={() => goToStep(isSetAside ? "new" : "future")}
				goalPortfolios={goal.portfolios}
				pinningId={pinningId}
			/>
			<NewContractFlow
				open={!readOnly && pinStep === "new"}
				goal={goal}
				onClose={closePin}
				onPinCreated={handlePinCreated}
			/>
		</>
	);
};
